import { Subscription } from 'rxjs';
import {
  connectGanCube, cubeTimestampCalcSkew, GanCubeConnection, GanCubeEvent, GanCubeMove
} from "gan-web-bluetooth";
import { ICubingService, StackmatState } from './ICubingService';
import { Alg } from 'cubing/alg';
import { cube3x3x3 } from 'cubing/puzzles';
import { KPuzzle } from 'cubing/kpuzzle';
import { createStartAnimationLoop } from './timerUtils';
import { faceletsToPattern } from '../utils/utils';
import { experimentalSolve3x3x3IgnoringCenters } from 'cubing/search';

type StackmatCallback = (state: StackmatState) => void;

const SOLVED_STATE = "UUUUUUUUURRRRRRRRRFFFFFFFFFDDDDDDDDDLLLLLLLLLBBBBBBBBB";

class BluetoothCubeService implements ICubingService {
  private connection: GanCubeConnection | null = null;
  private callback: StackmatCallback | null = null;
  private subscription: Subscription | undefined;
  private isInitialized = false;
  private solutionMoves: GanCubeMove[] = [];
  private startTime = 0;
  private deviceId = "unknown";
  private currentFacelets = SOLVED_STATE;


  private state: StackmatState = {
    timeMilli: 0,
    unit: 10,
    on: false,
    greenLight: false,
    leftHand: false,
    rightHand: false,
    running: false,
    unknownRunning: true,
    signalHeader: 'I',
    noise: 0,
    power: 1,
    deviceId: "unknown"
  };

  private tickInterval: number | null = null;
  private alg: Alg | null = null;

  // New fields for ticking timer
  private rafId: number | null = null;
  private animationStartOffset: number = 0;
  private cancelTickLoop: (() => void) | null = null;
  private kpuzzlePromise = cube3x3x3.kpuzzle();
  private bleBusy = false;

  private faceletsHandler: ((event: GanCubeEvent) => void) | null = null;

  public setFaceletListener(handler: (event: GanCubeEvent) => void) {
    this.faceletsHandler = handler;
  }

  constructor() {
    this.onEvent = this.onEvent.bind(this);
  }

  setCallback(cb: StackmatCallback) {
    this.callback = cb;
  }

  getCurrentState() {
    return this.state;
  }

  async validateFacelets(alg: Alg): Promise<boolean> {
    if (!this.connection) return false;

    // Wait for BLE to become available
    while (this.bleBusy) {
      await new Promise(r => setTimeout(r, 50)); // wait until it's free
    }

    this.bleBusy = true;

    try {
      const fresh = await awaitFreshFacelets(
        this.connection,
        this.setFaceletListener.bind(this)
      );

      if (!fresh || fresh.length !== 54) return false;

      this.currentFacelets = fresh;

      const pattern = faceletsToPattern(fresh);
      const solution = await experimentalSolve3x3x3IgnoringCenters(pattern);
      const kpuzzle = await this.kpuzzlePromise;
      const scramble = alg ?? new Alg();

      console.log("Raw facelets:", fresh);
      console.log("Expected:", alg?.toString());
      console.log("Solution:", solution.toString());

      return isIdentical(kpuzzle, scramble, solution.invert());
    } catch (err) {
      console.error("Validation error", err);
      return false;
    } finally {
      this.bleBusy = false;
    }
  }


  setReady(alg: Alg) {
    this.alg = alg;
    this.solutionMoves = [];

    this.state = {
      ...this.state,
      signalHeader: "H",   // ready to detect twist
      running: false,
      timeMilli: 0,
    };

    this.callback?.(this.state);
  }

  async init(type: string): Promise<void> {
    if (this.isInitialized) {
      this.state = {
        ...this.state,
        on: true
      }
      return;
    }

    try {
      this.connection = await connectGanCube(autoRetrieveMacAddress);
      this.subscription = this.connection.events$.subscribe(this.onEvent);
      await this.connection.sendCubeCommand({ type: "REQUEST_HARDWARE" });
      await this.connection.sendCubeCommand({ type: "REQUEST_FACELETS" });
      await this.connection.sendCubeCommand({ type: "REQUEST_BATTERY" });

      localStorage.setItem(`selectedCubeMac-${this.connection.deviceName}`, this.connection.deviceMAC);

      this.isInitialized = true;
      this.state = {
        ...this.state,
        on: true
      }

    } catch (err) {
      console.error("Bluetooth connection failed", err);
      throw err;
    }
  }

  async onEvent(event: GanCubeEvent) {
    switch (event.type) {
      case "HARDWARE":
        this.state = {
          ...this.state,
          deviceId: event.hardwareName ?? this.deviceId
        }
        break;
      case "BATTERY":
        this.state = {
          ...this.state,
          power: event.batteryLevel / 100
        }
        break;
      case "FACELETS":
        if (event.type === "FACELETS" && this.faceletsHandler) {
          this.faceletsHandler(event);
        }
        break;
      case "MOVE":
        this.solutionMoves.push(event);

        if (!this.state.running) {
          if (this.state.signalHeader === "H") {
            this.startTime = event.cubeTimestamp!;
            this.animationStartOffset = performance.now(); // - this.startTime;

            this.state = {
              ...this.state,
              signalHeader: " ",
              running: true,
              timeMilli: 0,
            };

            // this.startAnimationLoop()
            this.cancelTickLoop = createStartAnimationLoop(() => this.state.running,
              () => this.state,
              (newState) => {
                this.state = newState;
                this.callback?.(newState);
              }, this.animationStartOffset);

          } else {
            const kpuzzle = await this.kpuzzlePromise;
            const scramble = this.alg ?? new Alg();
            const algString = this.solutionMoves.map((m: GanCubeMove) => m.move).join(" ");
            const userMovesAlg = new Alg(algString);

            const isReady = isIdentical(kpuzzle, scramble, userMovesAlg);

            if (isReady) {
              this.state = {
                ...this.state,
                signalHeader: "H",
                timeMilli: 0,
              };
              this.solutionMoves = [];
            }
          }
        } else {
          const scramble = this.alg ?? new Alg();
          const algString = this.solutionMoves.map((m: GanCubeMove) => m.move).join(" ");
          const userMovesAlg = new Alg(algString);
          const totalAlg = new Alg(scramble.toString() + " " + userMovesAlg.toString());
          const kpuzzle = await this.kpuzzlePromise;

          const isSolved = isIdentical(kpuzzle, totalAlg, new Alg(""));
          const newTime = event.cubeTimestamp! - this.startTime;

          if (isSolved && this.state.running) {
            if (this.rafId !== null) {
              cancelAnimationFrame(this.rafId);
              this.rafId = null;
            }

            this.state = {
              ...this.state,
              signalHeader: "I",
              running: false,
              timeMilli: newTime,
              noise: cubeTimestampCalcSkew(this.solutionMoves)
            };

            this.solutionMoves = [];
            this.callback?.(this.state);
            return;
          }

          // Otherwise: correct timeMilli based on cube timestamp
          this.state = {
            ...this.state,
            timeMilli: newTime,
            noise: cubeTimestampCalcSkew(this.solutionMoves)
          };
          console.log(newTime);
          this.callback?.(this.state);
        }
    }
  }

  stop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.tickInterval) {
      clearInterval(this.tickInterval);
    }

    this.connection?.disconnect();
    this.subscription?.unsubscribe();
    this.connection = null;
    this.subscription = undefined;

    this.isInitialized = false;
    this.startTime = 0;
    this.animationStartOffset = 0;
    if (this.cancelTickLoop) {
      this.cancelTickLoop();
      this.cancelTickLoop = null;
    }

    this.state = {
      timeMilli: 0,
      unit: 10,
      on: false,
      greenLight: false,
      leftHand: false,
      rightHand: false,
      running: false,
      unknownRunning: true,
      signalHeader: 'I',
      noise: 0,
      power: 1,
      deviceId: "unknown"
    };
  }
}

const isIdentical = (kpuzzle: KPuzzle, alg1: Alg, alg2: Alg): boolean => {
  const transformation = kpuzzle.algToTransformation(alg1);
  const resultingPattern = transformation.toKPattern();
  const solvedTransformation = kpuzzle.algToTransformation(alg2);
  const solvedPattern = solvedTransformation.toKPattern();
  return resultingPattern.isIdentical(solvedPattern);
};

export const bluetoothCubeService = new BluetoothCubeService();

async function autoRetrieveMacAddress(device: BluetoothDevice, fallback?: boolean): Promise<string | null> {
  const promptMsg = 'Please enter the MAC address of your smart hardware (xx:xx:xx:xx:xx:xx). You can find the MAC address through chrome://bluetooth-internals/#devices, or modify following options to automatically obtain it:\nChrome: Turn on chrome://flags/#enable-experimental-web-platform-features\nBluefy: Turn on Enable BLE Advertisements';
  let mac = localStorage.getItem(`selectedCubeMac-${device.name}`);

  return new Promise<string | null>((resolve) => {
    if (mac !== null) return resolve(mac);

    if (fallback) {
      mac = prompt(promptMsg, mac || 'xx:xx:xx:xx:xx:xx');
    }

    resolve(mac || null);
  });
}

export async function awaitFreshFacelets(
  connection: GanCubeConnection,
  setFaceletListener: (cb: (event: GanCubeEvent) => void) => void,
  timeoutMs = 1500
): Promise<string | null> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);

    const handler = (event: GanCubeEvent) => {
      if (event.type === "FACELETS" && event.facelets) {
        clearTimeout(timeout);
        cleanup();
        resolve(event.facelets);
      }
    };

    const cleanup = () => {
      setFaceletListener(() => { }); // no-op
    };

    setFaceletListener(handler);
    connection.sendCubeCommand({ type: "REQUEST_FACELETS" }).catch((err) => {
      console.error("GATT error during facelet request:", err);
    });
  });
}